import { getApiAccessToken } from "../helpers/localStorage";
import ky, { HTTPError } from "ky";
import { captureException, withScope } from "@sentry/react";

interface ErrorResponse extends HTTPError {
  data?: unknown;
}

export type Api = typeof ky;

export const api = ky.create({
  prefixUrl: `${import.meta.env.VITE_API_URL_PROD}/api/`,
  headers: {
    "X-Requested-With": "XMLHttpRequest",
    "Access-Control-Allow-Origin": "*",
  },
  timeout: false,
  hooks: {
    beforeRequest: [
      (request) => {
        const token = getApiAccessToken();
        if (token) {
          request.headers.set("Authorization", `Bearer ${token}`);
        }
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        if (response.status !== 200) {
          return response;
        }
        const data = await response.json();
        return new Response(JSON.stringify(data), response);
      },
    ],
    beforeError: [
      async (error: ErrorResponse) => {
        if (error.response) {
          try {
            const errorData = await error.response.json();
            error.data = errorData;

            // Capture API errors in Sentry
            withScope((scope) => {
              scope.setTag("type", "api-error");
              scope.setExtra("url", error.response.url);
              scope.setExtra("status", error.response.status);
              scope.setExtra("method", error.response.statusText);
              scope.setExtra("data", errorData);

              captureException(error);
            });
          } catch {
            // If response is not JSON, still capture
            withScope((scope) => {
              scope.setTag("type", "api-network-error");
              captureException(error);
            });
          }
        } else {
          // Network errors (timeout, DNS, etc.)
          withScope((scope) => {
            scope.setTag("type", "api-network-error");
            captureException(error);
          });
        }

        return error;
      },
    ],
  },
});
